import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Alert from 'antd/es/alert';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import React from 'react';
import { styled } from 'styled-components';

import type {
  JoinBlockConfig,
  PipelineBlock,
  PipelineConnection,
} from '@mai/types';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface JoinBlockEditorProps {
  block: PipelineBlock & { type: 'join' };
  onConfigChange: (config: JoinBlockConfig) => void;
  connections?: PipelineConnection[];
  allBlocks?: Record<string, PipelineBlock>;
}

const joinTypes = [
  { value: 'inner', label: 'Inner Join' },
  { value: 'left', label: 'Left Join' },
  { value: 'right', label: 'Right Join' },
  { value: 'full', label: 'Full Join' },
];

const columnTypes = [
  { value: 'string', label: 'String' },
  { value: 'integer', label: 'Integer' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'null', label: 'Null' },
  { value: 'object', label: 'Object' },
];

const conflictResolutionStrategies = [
  { value: 'prefix', label: 'Prefix Strategy' },
  { value: 'suffix', label: 'Suffix Strategy' },
  { value: 'explicit', label: 'Explicit Strategy' },
];

export const JoinBlockEditor: React.FC<JoinBlockEditorProps> = ({
  block,
  onConfigChange,
  connections = [],
  allBlocks = {},
}) => {
  const [form] = Form.useForm();

  // Helper function to get connected datasets for this join block
  const getConnectedDatasets = React.useMemo(() => {
    const connectedDatasets: Array<{
      port: string;
      sourceBlockId: string;
      sourceBlock: PipelineBlock | null;
      currentName: string;
    }> = [];

    // Find all connections that target this join block
    const incomingConnections = connections.filter(
      (conn) => conn.target_block_id === block.id,
    );

    // Process each connection to extract dataset information
    incomingConnections.forEach((conn) => {
      // Only process dataset_X ports
      if (conn.target_port.startsWith('dataset_')) {
        const sourceBlock = allBlocks[conn.source_block_id] || null;
        const currentName =
          block.config.dataset_names?.[conn.target_port] || '';

        connectedDatasets.push({
          port: conn.target_port,
          sourceBlockId: conn.source_block_id,
          sourceBlock,
          currentName,
        });
      }
    });

    // Sort by port name to ensure consistent ordering (dataset_0, dataset_1, etc.)
    connectedDatasets.sort((a, b) => {
      const aNum = parseInt(a.port.replace('dataset_', ''));
      const bNum = parseInt(b.port.replace('dataset_', ''));
      return aNum - bNum;
    });

    return connectedDatasets;
  }, [connections, allBlocks, block.id, block.config.dataset_names]);

  // Helper function to generate default dataset name
  const generateDefaultDatasetName = (
    sourceBlock: PipelineBlock | null,
    sourceBlockId: string,
  ) => {
    if (!sourceBlock) return sourceBlockId;

    // Use the block's name if available, otherwise use the block ID
    const baseName = sourceBlock.id || sourceBlockId;

    // Create a readable name based on block type and ID
    switch (sourceBlock.type) {
      case 'dataset':
        return `${baseName}_data`;
      case 'input':
        return `${baseName}_input`;
      case 'constant':
        return `${baseName}_const`;
      case 'file':
        return `${baseName}_file`;
      default:
        return baseName;
    }
  };

  // Handler for dataset name changes
  const handleDatasetNameChange = (port: string, newName: string) => {
    const currentDatasetNames = block.config.dataset_names || {};
    const updatedDatasetNames = {
      ...currentDatasetNames,
      [port]: newName,
    };

    onConfigChange({
      ...block.config,
      dataset_names: updatedDatasetNames,
    });
  };

  // Initialize form with current values
  React.useEffect(() => {
    form.setFieldsValue({
      base_dataset: block.config.base_dataset || '',
      joins: block.config.joins || [],
      output_columns: block.config.output_columns || [],
      conflict_resolution: block.config.conflict_resolution || {
        strategy: 'prefix',
        left_modifier: '',
        right_modifier: '',
      },
    });
  }, [block.config, form]);

  const handleFormChange = (
    _: unknown,
    allValues: Partial<JoinBlockConfig>,
  ) => {
    onConfigChange({
      ...block.config,
      base_dataset: allValues.base_dataset || '',
      joins: allValues.joins || [],
      output_columns: allValues.output_columns || [],
      conflict_resolution: allValues.conflict_resolution || {
        strategy: 'prefix',
        left_modifier: '',
        right_modifier: '',
      },
    });
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleFormChange}
      >
        <Form.Item
          label="Base Dataset"
          name="base_dataset"
          help="Name of the base dataset to start joins from"
          rules={[{ required: true, message: 'Base dataset is required' }]}
        >
          <Input placeholder="Enter base dataset name" />
        </Form.Item>

        <EditorContainer>
          <EditorLabel>Dataset Names</EditorLabel>
          <EditorHelp>
            Assign logical names to connected datasets for use in joins and
            output columns
          </EditorHelp>

          {getConnectedDatasets.length === 0 ? (
            <Alert
              type="info"
              message="No datasets connected"
              description="Connect data sources to this join block to configure dataset names"
              showIcon
            />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {getConnectedDatasets.map(
                ({ port, sourceBlockId, sourceBlock, currentName }) => (
                  <div key={port}>
                    <EditorLabel
                      style={{ fontSize: '12px', marginBottom: '4px' }}
                    >
                      {port} ({sourceBlock?.type || 'unknown'}: {sourceBlockId})
                    </EditorLabel>
                    <Input
                      placeholder={generateDefaultDatasetName(
                        sourceBlock,
                        sourceBlockId,
                      )}
                      value={currentName}
                      onChange={(e) =>
                        handleDatasetNameChange(port, e.target.value)
                      }
                      style={{ marginBottom: '8px' }}
                    />
                  </div>
                ),
              )}

              {getConnectedDatasets.some((d) => !d.currentName) && (
                <Alert
                  type="warning"
                  message="Missing dataset names"
                  description="Some datasets don't have names assigned. Default names will be used."
                  showIcon
                />
              )}
            </Space>
          )}
        </EditorContainer>

        <Form.Item label="Join Operations">
          <Form.List name="joins">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'dataset']}
                        label="Dataset"
                        rules={[
                          { required: true, message: 'Dataset is required' },
                        ]}
                      >
                        <Input placeholder="Dataset name" />
                      </Form.Item>
                      <Space>
                        <Form.Item
                          {...restField}
                          name={[name, 'left_key']}
                          label="Left Key"
                          rules={[
                            { required: true, message: 'Left key is required' },
                          ]}
                        >
                          <Input placeholder="Left key" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'right_key']}
                          label="Right Key"
                          rules={[
                            {
                              required: true,
                              message: 'Right key is required',
                            },
                          ]}
                        >
                          <Input placeholder="Right key" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'join_type']}
                          label="Join Type"
                        >
                          <Select placeholder="Select join type">
                            {joinTypes.map((option) => (
                              <Select.Option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Space>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      >
                        Remove Join
                      </Button>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  Add Join
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label="Output Columns">
          <Form.List name="output_columns">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Column Name"
                          rules={[
                            {
                              required: true,
                              message: 'Column name is required',
                            },
                          ]}
                        >
                          <Input placeholder="Column name" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'source']}
                          label="Source"
                          rules={[
                            { required: true, message: 'Source is required' },
                          ]}
                        >
                          <Input placeholder="dataset.field" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'type']}
                          label="Type"
                          rules={[
                            { required: true, message: 'Type is required' },
                          ]}
                        >
                          <Select placeholder="Select column type">
                            {columnTypes.map((option) => (
                              <Select.Option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Space>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      >
                        Remove Column
                      </Button>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  Add Output Column
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>

        <EditorContainer>
          <EditorLabel>Conflict Resolution</EditorLabel>
          <EditorHelp>
            Configure how to handle column name conflicts during joins
          </EditorHelp>

          <Form.Item
            label="Strategy"
            name={['conflict_resolution', 'strategy']}
            help="Strategy for resolving column name conflicts"
          >
            <Select placeholder="Select strategy">
              {conflictResolutionStrategies.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Space>
            <Form.Item
              label="Left Modifier"
              name={['conflict_resolution', 'left_modifier']}
              help="Modifier for left dataset columns"
            >
              <Input placeholder="left_" />
            </Form.Item>
            <Form.Item
              label="Right Modifier"
              name={['conflict_resolution', 'right_modifier']}
              help="Modifier for right dataset columns"
            >
              <Input placeholder="right_" />
            </Form.Item>
          </Space>
        </EditorContainer>
      </Form>
    </>
  );
};
